import * as R from 'ramda';

import {
  AssetDetail,
  AssetElement,
  AssetNodeChangeState,
  ChangeType,
} from '@atom/types/asset';
import { isElementsChanged } from '@atom/utilities/assetUtilities';

export type NodeLoadStatus = 'unloaded' | 'loading' | 'loaded' | 'error';

export type SubItemContentGroup = {
  key: string;
  id: string;
  name: string;
  elementIds: string[];
};

export type SubItemContents = {
  ungroupedIds: string[];
  groups: SubItemContentGroup[];
};

export type SubItemTreeState = {
  nodes: Record<string, AssetElement>;
  contents: Record<string, SubItemContents>;
  details: Record<string, AssetDetail>;
  status: Record<string, NodeLoadStatus>;
  expanded: Record<string, boolean>;
};

// A fully resolved tree (e.g. an eager REST payload) ready to replace the
// store wholesale.
export type SubItemTreeSnapshot = {
  nodes: Record<string, AssetElement>;
  contents: Record<string, SubItemContents>;
  details: Record<string, AssetDetail>;
};

export const initialTreeState: SubItemTreeState = {
  nodes: {},
  contents: {},
  details: {},
  status: {},
  expanded: {},
};

// The part of a tree a surface may keep across unmounts: which rows are open
// and which is selected. Tree data is always re-read.
export type SubItemTreeViewState = {
  expanded: Record<string, boolean>;
  selectedId: string;
};

// `useReducer` initializer: an empty tree that already has the rows the user
// last opened, so the first `SET_TREE` keeps them open.
export const initTreeState = (
  expanded: Record<string, boolean> = {},
): SubItemTreeState => ({ ...initialTreeState, expanded });

export enum SubItemTreeActionType {
  NODE_LOADING = 'NODE_LOADING',
  NODE_LOADED = 'NODE_LOADED',
  NODE_ERROR = 'NODE_ERROR',
  TOGGLE_EXPANDED = 'TOGGLE_EXPANDED',
  EXPAND_PATH = 'EXPAND_PATH',
  SET_CHANGE_TYPES = 'SET_CHANGE_TYPES',
  REMOVE_NODE = 'REMOVE_NODE',
  SET_TREE = 'SET_TREE',
  RESET = 'RESET',
}

export type SubItemTreeAction =
  | { type: SubItemTreeActionType.NODE_LOADING; data: { id: string } }
  | {
      type: SubItemTreeActionType.NODE_LOADED;
      data: { id: string; detail: AssetDetail };
    }
  | { type: SubItemTreeActionType.NODE_ERROR; data: { id: string } }
  | { type: SubItemTreeActionType.TOGGLE_EXPANDED; data: { key: string } }
  | { type: SubItemTreeActionType.EXPAND_PATH; data: { ids: string[] } }
  | {
      type: SubItemTreeActionType.SET_CHANGE_TYPES;
      data: AssetNodeChangeState[];
    }
  | { type: SubItemTreeActionType.REMOVE_NODE; data: { id: string } }
  | { type: SubItemTreeActionType.SET_TREE; data: SubItemTreeSnapshot }
  | { type: SubItemTreeActionType.RESET };

export const groupKey = (parentId: string, groupId: string): string =>
  `${parentId}::${groupId}`;

const contentIds = (contents?: SubItemContents): string[] =>
  contents
    ? [
        ...contents.ungroupedIds,
        ...contents.groups.flatMap(group => group.elementIds),
      ]
    : [];

export const detailChildIds = (detail: AssetDetail): string[] =>
  [
    ...(detail.elements ?? []),
    ...(detail.elementGroups ?? []).flatMap(group => group.elements),
  ].map(element => element.id);

export const parentOf = (
  state: SubItemTreeState,
  id: string,
): string | undefined =>
  Object.keys(state.contents).find(parentId =>
    contentIds(state.contents[parentId]).includes(id),
  );

export const ancestorsOf = (state: SubItemTreeState, id: string): string[] => {
  const parentId = parentOf(state, id);
  return parentId ? [parentId, ...ancestorsOf(state, parentId)] : [];
};

export const collectSubtreeIds = (
  state: SubItemTreeState,
  id: string,
): string[] => [
  id,
  ...contentIds(state.contents[id]).flatMap(childId =>
    collectSubtreeIds(state, childId),
  ),
];

const isPendingNew = (changeType?: ChangeType): boolean =>
  changeType === ChangeType.ADDED || changeType === ChangeType.PARENT_ADDED;

export const hasDownstreamChanges = (
  state: SubItemTreeState,
  id: string,
): boolean => {
  const node = state.nodes[id];

  const serverReportsDescendantChanges = Boolean(node?.hasDescendantChanges);
  const onlyDescendantsChanged = isElementsChanged(node?.changeType);
  const isPendingNewWithChildren =
    isPendingNew(node?.changeType) && Boolean(node?.hasChildren);
  const hasChangedChild = (): boolean =>
    contentIds(state.contents[id]).some(childId =>
      Boolean(state.nodes[childId]?.changeType),
    );

  return (
    serverReportsDescendantChanges ||
    onlyDescendantsChanged ||
    isPendingNewWithChildren ||
    hasChangedChild()
  );
};

const withoutSubtree = (
  state: SubItemTreeState,
  id: string,
): SubItemTreeState => {
  const removedIds = collectSubtreeIds(state, id);
  const removedSet = new Set(removedIds);
  const isRemovedExpansionKey = (key: string): boolean =>
    removedSet.has(key) ||
    removedIds.some(removedId => key.startsWith(`${removedId}::`));

  return {
    ...state,
    nodes: R.omit(removedIds, state.nodes),
    contents: R.omit(removedIds, state.contents),
    details: R.omit(removedIds, state.details),
    status: R.omit(removedIds, state.status),
    expanded: R.pickBy(
      (_, key) => !isRemovedExpansionKey(String(key)),
      state.expanded,
    ),
  };
};

const buildContents = (id: string, detail: AssetDetail): SubItemContents => ({
  ungroupedIds: (detail.elements ?? []).map(element => element.id),
  groups: (detail.elementGroups ?? []).map(group => ({
    key: groupKey(id, group.id),
    id: group.id,
    name: group.name,
    elementIds: group.elements.map(element => element.id),
  })),
});

const nodeLoading = (
  state: SubItemTreeState,
  { id }: { id: string },
): SubItemTreeState => ({
  ...state,
  status: { ...state.status, [id]: 'loading' },
});

const nodeError = (
  state: SubItemTreeState,
  { id }: { id: string },
): SubItemTreeState => ({
  ...state,
  status: { ...state.status, [id]: 'error' },
});

const nodeLoaded = (
  state: SubItemTreeState,
  { id, detail }: { id: string; detail: AssetDetail },
): SubItemTreeState => {
  const freshElements = [
    ...(detail.elements ?? []),
    ...(detail.elementGroups ?? []).flatMap(group => group.elements),
  ];
  const freshIds = new Set(freshElements.map(element => element.id));

  const removedIds = contentIds(state.contents[id]).filter(
    childId => !freshIds.has(childId),
  );
  const pruned = removedIds.reduce(withoutSubtree, state);
  const existing = pruned.nodes[id];

  return {
    ...pruned,
    nodes: {
      ...pruned.nodes,
      ...R.indexBy(R.prop('id'), freshElements),
      ...(existing && {
        [id]: {
          ...existing,
          name: detail.name,
          changeType: detail.changeType,
          includeChangesBefore: detail.includeChangesBefore,
          hasChildren: !R.isEmpty(freshElements),
        },
      }),
    },
    contents: { ...pruned.contents, [id]: buildContents(id, detail) },
    details: { ...pruned.details, [id]: detail },
    status: { ...pruned.status, [id]: 'loaded' },
  };
};

const removeFromContents = (
  contents: SubItemContents,
  id: string,
): SubItemContents => ({
  ungroupedIds: contents.ungroupedIds.filter(elementId => elementId !== id),
  groups: contents.groups
    .map(group => ({
      ...group,
      elementIds: group.elementIds.filter(elementId => elementId !== id),
    }))
    .filter(group => !R.isEmpty(group.elementIds)),
});

const removeNode = (
  state: SubItemTreeState,
  { id }: { id: string },
): SubItemTreeState => {
  const parentId = parentOf(state, id);
  const pruned = withoutSubtree(state, id);

  if (!parentId) {
    return pruned;
  }

  const parentContents = removeFromContents(pruned.contents[parentId], id);
  const parentNode = pruned.nodes[parentId];

  return {
    ...pruned,
    contents: { ...pruned.contents, [parentId]: parentContents },
    nodes: parentNode
      ? {
          ...pruned.nodes,
          [parentId]: {
            ...parentNode,
            hasChildren: !R.isEmpty(contentIds(parentContents)),
          },
        }
      : pruned.nodes,
  };
};

const toggleExpanded = (
  state: SubItemTreeState,
  { key }: { key: string },
): SubItemTreeState => ({
  ...state,
  expanded: { ...state.expanded, [key]: !state.expanded[key] },
});

// `ids` is the full path root → … → target. The root's children are always
// visible and the target itself stays collapsed; everything in between is
// expanded, plus the group key wherever the next node on the path sits
// inside a group.
const expandPath = (
  state: SubItemTreeState,
  { ids }: { ids: string[] },
): SubItemTreeState => {
  const path = R.uniq(ids);
  const pairGroupKeys = R.zip(path, R.tail(path)).flatMap(
    ([parentId, childId]) => {
      const group = (state.contents[parentId]?.groups ?? []).find(candidate =>
        candidate.elementIds.includes(childId),
      );
      return group ? [group.key] : [];
    },
  );
  const nodeIds = path.slice(1, -1);

  return {
    ...state,
    expanded: [...nodeIds, ...pairGroupKeys].reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      state.expanded,
    ),
  };
};

const setChangeTypes = (
  state: SubItemTreeState,
  data: AssetNodeChangeState[],
): SubItemTreeState => ({
  ...state,
  nodes: data.reduce(
    (acc, { assetId, changeType, hasDescendantChanges }) =>
      acc[assetId]
        ? {
            ...acc,
            [assetId]: {
              ...acc[assetId],
              changeType: changeType ?? undefined,
              hasDescendantChanges,
            },
          }
        : acc,
    state.nodes,
  ),
});

// Replaces every node, keeping expansion state so a refetch doesn't collapse
// the rows the user opened.
const setTree = (
  state: SubItemTreeState,
  { nodes, contents, details }: SubItemTreeSnapshot,
): SubItemTreeState => ({
  nodes,
  contents,
  details,
  status: R.map(() => 'loaded' as NodeLoadStatus, details),
  expanded: state.expanded,
});

const subItemTreeReducer = (
  state: SubItemTreeState,
  action: SubItemTreeAction,
): SubItemTreeState => {
  switch (action.type) {
    case SubItemTreeActionType.NODE_LOADING:
      return nodeLoading(state, action.data);
    case SubItemTreeActionType.NODE_LOADED:
      return nodeLoaded(state, action.data);
    case SubItemTreeActionType.NODE_ERROR:
      return nodeError(state, action.data);
    case SubItemTreeActionType.TOGGLE_EXPANDED:
      return toggleExpanded(state, action.data);
    case SubItemTreeActionType.EXPAND_PATH:
      return expandPath(state, action.data);
    case SubItemTreeActionType.SET_CHANGE_TYPES:
      return setChangeTypes(state, action.data);
    case SubItemTreeActionType.REMOVE_NODE:
      return removeNode(state, action.data);
    case SubItemTreeActionType.SET_TREE:
      return setTree(state, action.data);
    case SubItemTreeActionType.RESET:
      return initialTreeState;
    default:
      return state;
  }
};

export default subItemTreeReducer;
